import { getApi } from '../api';
import { getDeviceAndUserStore, getAccountStore } from '../storage/stores';
import { captureException } from '@sentry/browser';
import { obtainFcmToken } from './obtainFcmToken';
import { ensurePushSubscription, isFirebaseMessagingReady } from './messaging';
import { PUSH_TAG } from './constants';

/**
 * Acquire a fresh FCM token and sync it to the backend.
 *
 * `ensurePushSubscription` must run before `obtainFcmToken` so our subscription
 * config (`userVisibleOnly: false`) wins over Firebase's default.
 */
export const ensureFcmTokenRegistered = async (): Promise<void> => {
  try {
    const ready = await isFirebaseMessagingReady();
    if (!ready) {
      console.warn(
        `${PUSH_TAG} Firebase Messaging not ready; skipping token registration`
      );
      return;
    }

    await ensurePushSubscription();
    await obtainFcmToken();
    await syncFcmTokenToBackend();
  } catch (error) {
    const err = error as Error;
    console.error(`${PUSH_TAG} Token registration failed:`, err.message);
    captureException(err);
  }
};

/**
 * Sync FCM token to backend
 *
 * Sends stored token to backend API. Idempotent: skips if token already sent.
 * Updates fcm.lastSentToken on success.
 *
 * @throws Error when stores are not initialized or backend call fails
 */
export const syncFcmTokenToBackend = async (): Promise<void> => {
  const deviceUserData = getDeviceAndUserStore().get();

  if (!deviceUserData?.fcm?.pushToken) {
    throw new Error('No FCM token found in storage');
  }

  const { pushToken: token, lastSentToken } = deviceUserData.fcm;

  if (lastSentToken === token) {
    return;
  }

  const accountData = getAccountStore().get();
  if (!accountData) {
    throw new Error('Account store not initialized');
  }

  await getApi().pushNotificationToken.post(
    {
      platform: 'FCM_Chrome',
      registration_id: token,
    } as const,
    {},
    {
      accountId: accountData.accountId,
      deviceUid: deviceUserData.device,
      userUid: deviceUserData.user,
    }
  );

  await getDeviceAndUserStore().set({
    fcm: {
      ...deviceUserData.fcm,
      lastSentToken: token,
    },
  });
};
